import * as Yup from "yup";
import React, { useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import apiService from "../services/ApiService";
import TailwindInput from "./Tailwind/TailwindInput";
import TailwindButton from "./Tailwind/TailwindButton";
import { cn } from "../lib/utils";

const SignupFormInvitationSharedModel = ({ register_succeeds, email }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState({
    message: '',
    variant: ''
  });

  const SignupSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("First name required"),
    lastName: Yup.string()
      .min(1, "Too Short!")
      .max(50, "Too Long!")
      .required("Last name required"),
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: email,
      password: "",
    },
    validationSchema: SignupSchema,
    onSubmit: () => {
      const passwordValidation = new RegExp(
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/
      );
      const isValidPassword = passwordValidation.test(values.password);
      if (isValidPassword) {
        setSubmitError({ message: '', variant: '' });
        setTimeout(() => {
          const sp = new URLSearchParams(window.location.search);
          apiService.registerFromSharedModel(values.firstName, values.lastName, values.email, values.password).then((response) => {
            if (response) {
              register_succeeds();
              sp.delete('access_token');
              sp.delete('token_type');
              sp.delete('context');
              window.location.search = sp.toString();
            }
          });
        }, 500);
      } else {
        formik.setSubmitting(false);
        setSubmitError({
          message: "The new password must contain 8 characters with at least one lowercase, one uppercase, one digit and one special character",
          variant: 'error'
        });
      }
    },
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <TailwindInput
              fullWidth
              label="First name"
              placeholder="John"
              {...getFieldProps("firstName")}
              error={touched.firstName && errors.firstName ? String(errors.firstName) : undefined}
            />
            <TailwindInput
              fullWidth
              label="Last name"
              placeholder="Doe"
              {...getFieldProps("lastName")}
              error={touched.lastName && errors.lastName ? String(errors.lastName) : undefined}
            />
          </div>

          <TailwindInput
            fullWidth
            autoComplete="username"
            type="email"
            label="Email address"
            {...getFieldProps("email")}
            readOnly
            disabled
            className="bg-slate-50"
          />

          <div className="relative">
            <TailwindInput
              fullWidth
              autoComplete="new-password"
              type={showPassword ? "text" : "password"}
              label="Password"
              placeholder="Create a password"
              {...getFieldProps("password")}
              error={touched.password && errors.password ? String(errors.password) : undefined}
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              <span className="material-symbols-outlined text-[20px]">
                {showPassword ? 'visibility' : 'visibility_off'}
              </span>
            </button>
          </div>

          <p className="text-[11px] text-slate-400 px-1 -mt-1">
            Must contain 8+ characters with uppercase, lowercase, digit and special character
          </p>

          {submitError.message && (
            <div className={cn(
              "flex items-center gap-2 p-3 rounded-lg text-sm",
              submitError.variant === 'error'
                ? "bg-red-50 text-red-800 border border-red-200"
                : "bg-green-50 text-green-800 border border-green-200"
            )}>
              <span className="material-symbols-outlined text-[18px] shrink-0">error</span>
              {submitError.message}
            </div>
          )}

          <TailwindButton
            fullWidth
            size="lg"
            type="submit"
            variant="dark"
            icon="person_add"
            disabled={isSubmitting}
            className="mt-1"
          >
            {isSubmitting ? 'Creating account...' : 'Create account'}
          </TailwindButton>
        </div>
      </Form>
    </FormikProvider>
  );
};

export default SignupFormInvitationSharedModel;
